import React, { useEffect, useRef, useState, useCallback } from 'react';
import { SessionStepProps, SessionStaticProps } from './SessionFlowBase';
import { Video } from '../VideoPlayer/VideoTypes';
import VideoView from '../VideoPlayer/VideoView';
import styled from 'styled-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDrag, useDrop } from 'react-dnd';
import InfoIcon from '@material-ui/icons/Info';
import { blue } from '@material-ui/core/colors';
import { TSession } from './Sessions';
import useSession, { refreshSession } from '../Util/useSession';
import sessionsService from '../../services/sessions';
import { timeSpanToSeconds } from '../../services/sessionModels';

interface Props extends SessionStepProps {}

const SessionVideoOrder: React.FunctionComponent<Props> & SessionStaticProps = ({ sessionid, setNextEnabled }) => {
  const { session } = useSession(sessionid);
  const divRef = React.useRef<HTMLDivElement>(null);
  const [videos, setVideos] = useState<Array<Readonly<Video>>>([]);

  useEffect(() => {
    if (session?.videos) {
      setVideos(session.videos.slice().sort((a: Video, b: Video) => (a.sortOrder || 0) - (b.sortOrder || 0)));
    }
  }, [session]);

  const viewHorizontalPadding: number = 2;

  const fullWidth: number = divRef.current ? divRef.current.clientWidth : 0;
  const viewWidth: number = fullWidth / (session?.videos?.length || 1) - viewHorizontalPadding * 2;
  const viewHeight: number = divRef.current ? divRef.current.clientHeight : 0;

  useEffect(() => {
    setNextEnabled(true);
  }, [setNextEnabled]);

  const findVideo = useCallback((videos: Array<Video>, videoid: string) => {
    const video = videos.filter((p) => String(p.id) === videoid)[0];
    const index = videos.indexOf(video);
    return { video, index };
  }, []);

  const moveVideo = useCallback(
    (a: string, b: string) => {
      const { video: dragVideo, index: dragIndex } = findVideo(videos, a);
      const { index: overIndex } = findVideo(videos, b);
      //   const dragPlayer = starting11[dragIndex];
      const ret = [...videos];
      const deleted = ret.splice(overIndex, 1, dragVideo);
      ret[dragIndex] = deleted[0];
      setVideos(ret);
    },
    [findVideo, videos]
  );

  const updateVideos = useCallback(() => {
    sessionsService
      .updateVideoOrder(videos.map((video) => video.id))
      .then(() => refreshSession(sessionid));
  }, [sessionid, videos]);

  return (
    <div>
      <InstructionContainer>
        <InfoIcon color="primary" style={{ fontSize: '30px', color: blue[500] }} />
        <Instruction>Drag the videos in the correct order from left to right</Instruction>
      </InstructionContainer>
      <Container ref={divRef}>
        <DndProvider backend={HTML5Backend}>
          {videos.map((video: Video, index) => (
            <DraggableVideo
              key={video.id}
              video={video}
              width={viewWidth}
              height={viewHeight}
              moveVideo={moveVideo}
              updateVideos={updateVideos}
              viewHorizontalPadding={viewHorizontalPadding}
            />
          ))}
        </DndProvider>
      </Container>
    </div>
  );
};

type VideoProps = {
  video: Video;
  width: number;
  height: number;
  moveVideo: (a: string, b: string) => void;
  updateVideos: () => void;
  viewHorizontalPadding: number;
};

interface DragItem {
  id: string;
}

const DraggableVideo: React.FunctionComponent<VideoProps> = ({
  video,
  width,
  height,
  moveVideo,
  updateVideos,
  viewHorizontalPadding,
}) => {
  const ref = useRef(null);
  const [{ isDragging }, drag] = useDrag({
    type: 'VIDEO',
    item: () => {
      return { id: String(video.id) };
    },
    // The collect function utilizes a "monitor" instance (see the Overview for what this is)
    // to pull important pieces of state from the DnD system.
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'VIDEO',
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    hover(item: DragItem, monitor) {
      if (item.id && item.id !== String(video.id)) {
        moveVideo(item.id, String(video.id));
      }
    },
    drop() {
      updateVideos();
    },
  });

  drag(drop(ref));
  const videoPlayTime: number = 1 + timeSpanToSeconds(video?.syncOffset);
  return (
    <div
      ref={ref}
      key={video.id}
      style={{ margin: '0px ' + viewHorizontalPadding + 'px', opacity: isDragging ? 0.1 : 1 }}>
      <VideoView
        id={String(video.id)}
        viewWidth={width}
        viewHeight={height}
        videoWidth={video?.width || 0}
        videoHeight={video?.height || 0}
        url={video.transcodeStatus?.streamUrl || ''}
        isPlaying={false}
        playTime={videoPlayTime}
        playbackSpeed={1}
        overlayItems={[]}
      />
    </div>
  );
};

const Container = styled.div`
  width: 100%;
  height: calc(100vh - 500px);
  display: flex;
  background-color: #f5f6fa;
`;

const Instruction = styled.div`
  font-size: 20px;
  margin: 20px;
`;

const InstructionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

SessionVideoOrder.isComplete = (session: TSession) => {
  return true;
};
export default SessionVideoOrder;
